import {Http} from '../../../Data/config/Http';
import {BaseResponse, Pagination} from '../../../Data/types/BaseResponse';
import {API_ROUTES} from '../../../constants/apiRoutes';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';
import {Checklist, ChecklistFilters, ChecklistPayload, ChecklistStatus, VisitChecklistSubmission} from '../models/checklist.model';

export interface ChecklistQueryParams extends ChecklistFilters {
  page?: number;
  limit?: number;
}

export const ChecklistAPIDataSource = {
  list: async (params?: ChecklistQueryParams): Promise<{ items: Checklist[]; pagination?: Pagination }> => {
    try {
      const response = await Http.get<BaseResponse<Checklist[]>>(API_ROUTES.CHECKLISTS.BASE, {
        params: {
          page: params?.page ?? PAGINATION_CONFIG.DEFAULT_PAGE,
          limit: params?.limit ?? PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
          ...(params?.status ? {status: params.status} : {}),
          ...(params?.channel ? {channel: params.channel} : {}),
          ...(params?.assignment_type ? {assignment_type: params.assignment_type} : {}),
          ...(params?.search ? {search: params.search} : {})
        }
      });
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب قوائم التحقق');
      return {items: Array.isArray(items) ? items : [], pagination: raw.pagination};
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getById: async (id: number): Promise<Checklist> => {
    try {
      const response = await Http.get<BaseResponse<Checklist>>(API_ROUTES.CHECKLISTS.BY_ID(id));
      return unwrapResponse(response.data, 'حدث خطأ في جلب قائمة التحقق');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // CR09 Phase 2 — filled checklists for a visit (per-visit dashboard view).
  listVisitSubmissions: async (visitId: number): Promise<VisitChecklistSubmission[]> => {
    try {
      const response = await Http.get<BaseResponse<VisitChecklistSubmission[]>>(
        API_ROUTES.CHECKLISTS.SUBMISSIONS,
        {params: {visit_id: visitId}}
      );
      const items = unwrapResponse(response.data, 'حدث خطأ في جلب نتائج قوائم التحقق');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  create: async (payload: ChecklistPayload): Promise<Checklist> => {
    try {
      const response = await Http.post<BaseResponse<Checklist>>(API_ROUTES.CHECKLISTS.BASE, payload);
      return unwrapResponse(response.data, 'فشل في إنشاء قائمة التحقق', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  update: async (id: number, payload: ChecklistPayload): Promise<Checklist> => {
    try {
      const response = await Http.put<BaseResponse<Checklist>>(API_ROUTES.CHECKLISTS.BY_ID(id), payload);
      return unwrapResponse(response.data, 'فشل في تحديث قائمة التحقق');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  setStatus: async (id: number, status: ChecklistStatus): Promise<Checklist> => {
    try {
      const response = await Http.patch<BaseResponse<Checklist>>(API_ROUTES.CHECKLISTS.STATUS(id), {status});
      return unwrapResponse(response.data, 'فشل في تحديث حالة قائمة التحقق');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  remove: async (id: number): Promise<void> => {
    try {
      const response = await Http.delete<BaseResponse<null>>(API_ROUTES.CHECKLISTS.BY_ID(id));
      unwrapResponse(response.data, 'فشل في حذف قائمة التحقق');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
